
import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { event as gaEvent } from '@/lib/gtag';

interface NavbarProps {
  topOffset?: boolean;
}

export default function Navbar({ topOffset = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = [
    { label: '서비스 소개', href: '#how' },
    { label: '요금제', href: '#pricing' },
    { label: '포트폴리오', href: '#portfolio' },
  ];

  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    gaEvent('click_cta', { location: 'navbar', button: 'start' });
    window.REACT_APP_NAVIGATE('/login', { state: { from: '/order' } });
  };

  const handleMyOrders = (e: React.MouseEvent) => {
    e.preventDefault();
    gaEvent('click_nav', { location: 'navbar', button: 'my_orders' });
    window.REACT_APP_NAVIGATE('/my-orders');
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    gaEvent('logout', { location: 'navbar' });
    logout();
    window.REACT_APP_NAVIGATE('/');
  };

  return (
    <nav className={`fixed left-0 right-0 z-50 transition-all duration-500 ${topOffset ? 'top-10' : 'top-0'} ${isScrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <a href="#" className="flex items-center gap-2 cursor-pointer">
          <span className={`font-serif text-2xl font-medium tracking-tight transition-colors duration-300 ${isScrolled ? 'text-[#2D2D2D]' : 'text-white'}`}>
            봄날
          </span>
          <span className={`text-xs tracking-[0.2em] font-light transition-colors duration-300 ${isScrolled ? 'text-[#C9A96E]' : 'text-white/80'}`}>
            AI WEDDING
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {links.map(l => (
            <a
              key={l.href}
              href={l.href}
              className={`text-sm font-light tracking-wide transition-all duration-300 cursor-pointer relative group whitespace-nowrap ${isScrolled ? 'text-gray-600 hover:text-[#C9A96E]' : 'text-white/90 hover:text-white'}`}
            >
              {l.label}
              <span className={`absolute -bottom-1 left-0 w-0 h-[1px] group-hover:w-full transition-all duration-300 ${isScrolled ? 'bg-[#C9A96E]' : 'bg-white'}`}></span>
            </a>
          ))}

          {user ? (
            <div className="flex items-center gap-4">
              <button
                onClick={handleMyOrders}
                className={`text-sm font-light tracking-wide transition-all duration-300 cursor-pointer relative group whitespace-nowrap flex items-center gap-1.5 ${isScrolled ? 'text-gray-600 hover:text-[#C9A96E]' : 'text-white/90 hover:text-white'}`}
              >
                <i className="ri-file-list-3-line text-base"></i>
                내 주문
                <span className={`absolute -bottom-1 left-0 w-0 h-[1px] group-hover:w-full transition-all duration-300 ${isScrolled ? 'bg-[#C9A96E]' : 'bg-white'}`}></span>
              </button>
              <div className={`flex items-center gap-2 text-sm font-light ${isScrolled ? 'text-gray-600' : 'text-white/80'}`}>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#C9A96E]/20">
                  <i className="ri-user-line text-[#C9A96E] text-xs"></i>
                </div>
                <span>{user.name}</span>
              </div>
              <button
                onClick={handleLogout}
                className={`text-sm font-light transition-colors duration-300 cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-400 hover:text-gray-600' : 'text-white/60 hover:text-white'}`}
              >
                로그아웃
              </button>
            </div>
          ) : (
            <button
              onClick={handleApplyClick}
              className="px-7 py-2.5 bg-[#C9A96E] text-white text-sm font-medium rounded-full hover:bg-[#b8935a] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg"
            >
              시작하기
            </button>
          )}
        </div>

        <button className="md:hidden w-9 h-9 flex items-center justify-center" onClick={() => setMenuOpen(v => !v)}>
          <i className={`text-2xl transition-colors duration-300 ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} ${isScrolled ? 'text-gray-800' : 'text-white'}`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {links.map(l => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="text-sm text-gray-700 font-light py-1 cursor-pointer">
              {l.label}
            </a>
          ))}
          {user ? (
            <>
              <button
                onClick={(e) => { handleMyOrders(e); setMenuOpen(false); }}
                className="text-sm text-gray-700 font-light py-1 cursor-pointer text-left flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                내 주문 내역
              </button>
              <button
                onClick={(e) => { handleLogout(e); setMenuOpen(false); }}
                className="text-sm text-gray-400 font-light py-1 cursor-pointer text-left whitespace-nowrap"
              >
                로그아웃
              </button>
            </>
          ) : (
            <button
              onClick={(e) => { handleApplyClick(e); setMenuOpen(false); }}
              className="mt-2 px-7 py-2.5 bg-[#C9A96E] text-white text-sm font-medium rounded-full text-center cursor-pointer whitespace-nowrap"
            >
              시작하기
            </button>
          )}
        </div>
      )}
    </nav>
  );
}
